import React, { createContext, useContext, useState, useEffect } from 'react';
import { getApiUrl } from '../config/api';
import i18n from '../i18n';

// Helper function to translate error codes
const translateError = (errorMessage: string): string => {
  // Check if the error message is an error code (starts with ERROR_)
  if (errorMessage && errorMessage.startsWith('ERROR_')) {
    const translationKey = `auth.errors.${errorMessage}`;
    const translated = i18n.t(translationKey);
    // If translation exists and is different from the key, use it
    if (translated && translated !== translationKey) {
      return translated;
    }
  }
  // Return original message if not a code or translation not found
  return errorMessage;
};

export interface UserSettings {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  company: string;
  vatNumber: string;
  kvkNumber: string;
  language: string;
  billingAddress: {
    company: string;
    name: string;
    street: string;
    number: string;
    postal: string;
    city: string;
    country: string;
  };
  deliveryAddress: {
    company: string;
    name: string;
    street: string;
    number: string;
    postal: string;
    city: string;
    country: string;
  };
}

export interface User {
  id: string;
  email: string;
  portal_id: string;
  status?: string;
  settings?: UserSettings;
}

export interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isCheckingAuth: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: (skipRedirect?: boolean) => Promise<void>;
  requestAccount: (email: string) => Promise<boolean>;
  requestPasswordReset: (email: string) => Promise<boolean>;
  resetPassword: (token: string, password: string) => Promise<boolean>;
  setNewAccountPassword: (token: string, password: string) => Promise<boolean>;
  updateUserSettings: (settings: UserSettings) => void;
  error: string | null;
  clearError: () => void;
  resetEmailSent: boolean;
  resetEmail: string | null;
  clearResetEmailSent: () => void;
  passwordResetSuccess: boolean;
  clearPasswordResetSuccess: () => void;
  newAccountPasswordSetSuccess: boolean;
  clearNewAccountPasswordSetSuccess: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetEmail, setResetEmail] = useState<string | null>(null);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [newAccountPasswordSetSuccess, setNewAccountPasswordSetSuccess] = useState(false);

  const isAuthenticated = !!user;

  // Load user settings and apply language
  const loadUserSettings = async (userObj: User) => {
    try {
      const response = await fetch(getApiUrl('/api/web/user/settings'), {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const result = await response.json();
        if (result.meta?.result === 'success' && result.data) {
          const updatedUser = { ...userObj, settings: result.data };
          setUser(updatedUser);
          
          // Apply language setting
          if (result.data.language) {
            i18n.changeLanguage(result.data.language);
          }
          
          return updatedUser;
        }
      }
    } catch (error) {
      console.warn('Failed to load user settings:', error);
    }
    return userObj;
  };

  const updateUserSettings = (settings: UserSettings) => {
    if (user) {
      const updatedUser = { ...user, settings };
      setUser(updatedUser);
      
      // Apply language setting
      if (settings.language) {
        i18n.changeLanguage(settings.language);
      }
    }
  };

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        // Check if there's a session cookie before making the API call
        console.log('Checking for session cookie...');
        console.log('All cookies:', document.cookie);

        const hasSessionCookie = document.cookie
          .split(';')
          .some(cookie => cookie.trim().startsWith('session_token='));

        console.log('Has session cookie:', hasSessionCookie);

        if (!hasSessionCookie) {
          // No session cookie, skip the API call and stop checking
          console.log('No session cookie found, skipping /me call');
          setIsCheckingAuth(false);
          return;
        }

        console.log('Making /me API call...');
        const response = await fetch(getApiUrl('/api/web/auth/me'), {
          credentials: 'include' // Include cookies
        });

        console.log('/me response status:', response.status);

        if (response.ok) {
          const userData = await response.json();
          console.log('/me response data:', userData);
          const userWithSettings = await loadUserSettings(userData.data);
          setUser(userWithSettings);
          console.log('User set successfully after /me call');
        } else {
          console.log('/me call failed with status:', response.status);
        }
      } catch (err) {
        console.error('Session check failed:', err);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkSession();
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setError(null);
      setIsLoading(true);

      const url = getApiUrl('/api/web/auth/login');
      console.log('Login URL:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      console.log('Login response status:', response.status);
      console.log('Login response URL:', response.url);
      console.log('Login response headers:', response.headers);
      console.log('Cookies after login:', document.cookie);

      const result = await response.json();
      console.log('Login result:', result);

      // Check both response.ok and meta.result for success/error
      if (response.ok && result.meta?.result === 'success') {
        console.log('Login successful, loading user settings...');
        const userWithSettings = await loadUserSettings(result.data.user);
        setUser(userWithSettings);
        console.log('Login complete, user set:', userWithSettings);

        // Check cookies again after successful login
        setTimeout(() => {
          console.log('Cookies 1 second after login:', document.cookie);
        }, 1000);

        return true;
      } else {
        // Try to get error message from meta.message first, then fallback to message
        const errorMessage = result.meta?.message || result.message || 'Login failed';
        setError(translateError(errorMessage));
        return false;
      }
    } catch (err) {
      setError('Network error during login');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (skipRedirect: boolean = false): Promise<void> => {
    try {
      await fetch(getApiUrl('/api/web/auth/logout'), {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('Logout request failed:', err);
    } finally {
      setUser(null);
      setIsLoading(false); // Stop loading state immediately
      // Redirect to base URL after logout (unless skipRedirect is true)
      if (!skipRedirect) {
        window.location.href = '/';
      }
    }
  };

  const requestAccount = async (email: string): Promise<boolean> => {
    try {
      setError(null);
      setIsLoading(true);

      const response = await fetch(getApiUrl('/api/web/auth/request-account'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          language: i18n.language || 'en'
        }),
      });

      const result = await response.json();

      // Check if response is OK and has success status in meta
      if (response.ok && result.meta?.result === 'success') {
        setResetEmailSent(true);
        setResetEmail(email);
        return true;
      } else {
        const errorMessage = result.meta?.message || result.message || 'Account request failed';
        setError(translateError(errorMessage));
        return false;
      }
    } catch (err) {
      setError('Network error during account request');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const requestPasswordReset = async (email: string): Promise<boolean> => {
    try {
      setError(null);
      setIsLoading(true);

      const response = await fetch(getApiUrl('/api/web/auth/request-password-reset'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          language: i18n.language || 'en'
        }),
      });

      const result = await response.json();

      // Check if response is OK and has success status in meta
      if (response.ok && result.meta?.result === 'success') {
        setResetEmailSent(true);
        setResetEmail(email);
        return true;
      } else {
        const errorMessage = result.meta?.message || result.message || 'Password reset request failed';
        setError(translateError(errorMessage));
        return false;
      }
    } catch (err) {
      setError('Network error during password reset request');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (token: string, password: string): Promise<boolean> => {
    try {
      setError(null);
      setIsLoading(true);

      const response = await fetch(getApiUrl('/api/web/auth/reset-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, password }),
      });

      const result = await response.json();

      if (response.ok && result.meta?.result === 'success') {
        setPasswordResetSuccess(true);
        return true;
      } else {
        const errorMessage = result.meta?.message || result.message || 'Password reset failed';
        setError(translateError(errorMessage));
        return false;
      }
    } catch (err) {
      setError('Network error during password reset');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const setNewAccountPassword = async (token: string, password: string): Promise<boolean> => {
    try {
      setError(null);
      setIsLoading(true);

      const response = await fetch(getApiUrl('/api/web/auth/set-new-account-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, password }),
      });

      const result = await response.json();

      if (response.ok && result.meta?.result === 'success') {
        setNewAccountPasswordSetSuccess(true);
        return true;
      } else {
        const errorMessage = result.meta?.message || result.message || 'Failed to set password';
        setError(translateError(errorMessage));
        return false;
      }
    } catch (err) {
      setError('Network error during password setup');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const clearResetEmailSent = () => {
    setResetEmailSent(false);
    setResetEmail(null);
  };

  const clearPasswordResetSuccess = () => {
    setPasswordResetSuccess(false);
  };

  const clearNewAccountPasswordSetSuccess = () => {
    setNewAccountPasswordSetSuccess(false);
  };

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      isCheckingAuth,
      isAuthenticated,
      login,
      logout,
      requestAccount,
      requestPasswordReset,
      resetPassword,
      setNewAccountPassword,
      updateUserSettings,
      error,
      clearError,
      resetEmailSent,
      resetEmail,
      clearResetEmailSent,
      passwordResetSuccess,
      clearPasswordResetSuccess,
      newAccountPasswordSetSuccess,
      clearNewAccountPasswordSetSuccess
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}